import { useEffect } from 'react';
import { SITE_URL, SITE_NAME, SITE_LOGO } from '@/lib/structuredData';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  structuredData?: Record<string, any> | Record<string, any>[];
  keywords?: string;
  author?: string;
  type?: 'website' | 'article' | 'product';
  publishedTime?: string;
  modifiedTime?: string;
}

export const SEO = ({ 
  title, 
  description, 
  canonical, 
  ogImage = SITE_LOGO,
  noindex = false,
  structuredData,
  keywords,
  author = SITE_NAME,
  type = 'website',
  publishedTime,
  modifiedTime
}: SEOProps) => {
  useEffect(() => {
    // Update document title
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    // Helper to set or update meta tag
    const setMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Update meta tags
    setMetaTag('description', description);
    setMetaTag('author', author);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    
    // Open Graph
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:site_name', SITE_NAME, true);
    setMetaTag('og:url', canonical || window.location.href, true);
    
    if (publishedTime) {
      setMetaTag('article:published_time', publishedTime, true);
    }
    if (modifiedTime) {
      setMetaTag('article:modified_time', modifiedTime, true);
    }
    
    // Twitter
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    setMetaTag('twitter:site', '@getsentinelpro');

    // Handle noindex for secure pages
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Handle canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonical) {
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.rel = 'canonical';
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.href = canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`;
    } else if (linkCanonical) {
      // Update to current URL if no canonical specified
      linkCanonical.href = window.location.href.split('?')[0];
    }

    // Handle structured data - support multiple schemas
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
    existingScripts.forEach(script => script.remove());
    
    if (structuredData) {
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      dataArray.forEach((data, index) => {
        const scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        scriptTag.setAttribute('data-seo', 'true');
        scriptTag.textContent = JSON.stringify(data);
        document.head.appendChild(scriptTag);
      });
    }

    // Cleanup on unmount
    return () => {
      const seoScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
      seoScripts.forEach(script => script.remove());
    };
  }, [title, description, canonical, ogImage, noindex, structuredData, keywords, author, type, publishedTime, modifiedTime]);

  return null;
};
